import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { mfaApi } from '../hooks/useApi';
import { C } from '../components/UI';

export default function MfaSetupPage() {
  const navigate = useNavigate();
  const [step, setStep]       = useState('intro'); // intro | qr | confirm | done
  const [qrCode, setQrCode]   = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [code, setCode]       = useState('');
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  async function handleSetup() {
    setLoading(true); setError('');
    try {
      const data = await mfaApi.setup();
      setQrCode(data.qrCode);
      setSecretKey(data.secretKey);
      setStep('qr');
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur lors de la configuration.');
    } finally { setLoading(false); }
  }

  async function handleConfirm(e) {
    e.preventDefault();
    if (code.length !== 6) { setError('Code à 6 chiffres requis.'); return; }
    setLoading(true); setError('');
    try {
      await mfaApi.confirm(code);
      setStep('done');
    } catch (e) {
      setError(e.response?.data?.error || 'Code incorrect.');
      setCode('');
    } finally { setLoading(false); }
  }

  return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={S.logo}>
          <div style={S.logoTitle}>EBIOS RM</div>
          <div style={S.logoSub}>Risk Manager</div>
        </div>

        {step === 'intro' && (
          <>
            <div style={S.iconWrap}>🔐</div>
            <h1 style={S.title}>Activer l'authentification à deux facteurs</h1>
            <p style={S.desc}>
              La MFA ajoute une couche de sécurité supplémentaire. En plus de votre mot de passe,
              vous devrez entrer un code généré par une application d'authentification.
            </p>
            <div style={S.appList}>
              <div style={S.appItem}>📱 <strong>Google Authenticator</strong> — iOS / Android</div>
              <div style={S.appItem}>🔑 <strong>Authy</strong> — iOS / Android / Desktop</div>
              <div style={S.appItem}>🪟 <strong>Microsoft Authenticator</strong> — iOS / Android</div>
            </div>
            {error && <div style={S.error}>{error}</div>}
            <button style={S.btn} onClick={handleSetup} disabled={loading}>
              {loading ? 'Préparation...' : 'Configurer la MFA →'}
            </button>
            <button style={S.btnBack} onClick={() => navigate('/')}>Annuler</button>
          </>
        )}

        {step === 'qr' && (
          <>
            <h1 style={S.title}>Scannez le QR code</h1>
            <p style={S.desc}>Ouvrez votre app d'authentification et scannez ce QR code.</p>
            <div style={S.qrWrap}>
              <img src={qrCode} alt="QR Code MFA" style={{ width:200, height:200, borderRadius:8 }} />
            </div>
            <div style={S.manualToggle} onClick={() => setShowSecret(!showSecret)}>
              {showSecret ? '▲' : '▼'} Entrer la clé manuellement
            </div>
            {showSecret && (
              <div style={S.secretBox}>
                <div style={S.secretLabel}>Clé secrète :</div>
                <code style={S.secretCode}>{secretKey}</code>
                <div style={S.secretNote}>⚠️ Ne partagez jamais cette clé.</div>
              </div>
            )}
            <button style={S.btn} onClick={() => setStep('confirm')}>
              J'ai scanné le QR code →
            </button>
          </>
        )}

        {step === 'confirm' && (
          <>
            <h1 style={S.title}>Confirmer avec un code</h1>
            <p style={S.desc}>
              Entrez le code à 6 chiffres affiché dans votre application d'authentification
              pour confirmer que la configuration est correcte.
            </p>
            <form onSubmit={handleConfirm}>
              <div style={S.group}>
                <label style={S.label}>Code TOTP</label>
                <input
                  style={{...S.input, ...S.codeInput}}
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="000000"
                  autoFocus
                  value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                />
              </div>
              {error && <div style={S.error}>{error}</div>}
              <button type="submit" style={S.btn} disabled={loading || code.length !== 6}>
                {loading ? 'Vérification...' : 'Activer la MFA'}
              </button>
              <button type="button" style={S.btnBack} onClick={() => setStep('qr')}>← Retour</button>
            </form>
          </>
        )}

        {step === 'done' && (
          <>
            <div style={S.successIcon}>✅</div>
            <h1 style={S.title}>MFA activée avec succès</h1>
            <p style={S.desc}>
              Votre compte est maintenant protégé par l'authentification à deux facteurs.
              À chaque connexion, un code vous sera demandé.
            </p>
            <div style={S.warningBox}>
              ⚠️ <strong>Important :</strong> Si vous perdez accès à votre app d'authentification,
              vous ne pourrez plus vous connecter. Sauvegardez la clé secrète en lieu sûr.
            </div>
            <button style={S.btn} onClick={() => navigate('/')}>
              Retour au tableau de bord
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const S = {
  page:       { minHeight:'100vh', background:'var(--bg)', display:'flex', alignItems:'center', justifyContent:'center', padding:20, fontFamily:"'Segoe UI',system-ui,sans-serif" },
  card:       { background:'var(--bg)', border:'1px solid var(--border)', borderRadius:20, padding:'40px 36px', maxWidth:460, width:'100%' },
  logo:       { textAlign:'center', marginBottom:24 },
  logoTitle:  { fontFamily:'Geist,system-ui,sans-serif', fontSize:22, color:'var(--text)', letterSpacing:-1 },
  logoSub:    { fontSize:10, color:'var(--text3)', textTransform:'uppercase', letterSpacing:3, marginTop:3 },
  iconWrap:   { fontSize:40, textAlign:'center', marginBottom:16 },
  successIcon:{ fontSize:48, textAlign:'center', marginBottom:16 },
  title:      { fontSize:20, color:'var(--text)', marginBottom:12, fontWeight:600 },
  desc:       { fontSize:13, color:'var(--text2)', lineHeight:1.6, marginBottom:20 },
  appList:    { background:'var(--bg-muted)', borderRadius:10, padding:16, marginBottom:24 },
  appItem:    { fontSize:13, color:'var(--text2)', padding:'6px 0', borderBottom:`1px solid ${C.border}` },
  group:      { marginBottom:16 },
  label:      { display:'block', fontSize:11, color:'var(--text2)', textTransform:'uppercase', letterSpacing:.8, marginBottom:6, fontWeight:600 },
  input:      { width:'100%', background:'var(--bg-muted)', border:'1px solid var(--border)', color:'var(--text)', padding:'11px 14px', borderRadius:8, fontSize:14, outline:'none', boxSizing:'border-box', fontFamily:'inherit' },
  codeInput:  { fontSize:28, letterSpacing:10, textAlign:'center', fontFamily:'monospace' },
  qrWrap:     { display:'flex', justifyContent:'center', background:'white', borderRadius:12, padding:16, marginBottom:16 },
  manualToggle:{ fontSize:12, color:'var(--accent)', cursor:'pointer', textAlign:'center', marginBottom:12 },
  secretBox:  { background:'var(--bg-muted)', border:'1px solid var(--border)', borderRadius:8, padding:16, marginBottom:16 },
  secretLabel:{ fontSize:11, color:'var(--text3)', textTransform:'uppercase', letterSpacing:1, marginBottom:6 },
  secretCode: { display:'block', fontFamily:'monospace', fontSize:13, color:'var(--text)', wordBreak:'break-all', letterSpacing:2 },
  secretNote: { fontSize:11, color:'var(--warn)', marginTop:8 },
  error:      { background:'rgba(255,79,110,.1)', border:'1px solid rgba(255,79,110,.2)', color:'var(--danger)', padding:'10px 14px', borderRadius:8, fontSize:13, marginBottom:16 },
  btn:        { width:'100%', background:'var(--blue)', color:'white', border:'none', padding:12, borderRadius:8, fontSize:14, fontWeight:600, cursor:'pointer', marginTop:4, fontFamily:'inherit' },
  btnBack:    { width:'100%', background:'transparent', color:'var(--text3)', border:'1px solid var(--border)', padding:10, borderRadius:8, fontSize:13, cursor:'pointer', marginTop:8, fontFamily:'inherit' },
  warningBox: { background:'rgba(255,159,79,.08)', border:'1px solid rgba(255,159,79,.2)', color:'var(--warn)', borderRadius:8, padding:'12px 14px', fontSize:13, lineHeight:1.6, marginBottom:20 },
};
